import { useEffect, useRef, useState } from 'react';
import { toBlob } from 'html-to-image';
import { Download, Loader2 } from 'lucide-react';
import { useAllianceLabel } from '@/lib/useCurrentAlliance';

// Shared building blocks for the 1080×1350 PNG posters (Hall of Fame,
// Kill growth). Posters use inline styles only — html-to-image renders
// them outside the Tailwind cascade.

export const POSTER_W = 1080;
export const POSTER_H = 1350;

export async function downloadPoster(node: HTMLElement, filename: string) {
  // pixelRatio 1 → PNG is exactly POSTER_W × POSTER_H (1080×1350).
  // Blob + object URL works on iOS Safari where huge data: URLs open the
  // image in a new tab instead of downloading it.
  const blob = await toBlob(node, {
    width: POSTER_W,
    height: POSTER_H,
    pixelRatio: 1,
    cacheBust: true,
    backgroundColor: '#0b1220',
  });
  if (!blob) throw new Error('toBlob returned null');
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function PosterCard({
  title,
  description,
  busy,
  onExport,
  children,
}: {
  title: string;
  description: string;
  busy: boolean;
  onExport: () => void;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold">{title}</h2>
          <p className="text-xs text-slate-400">{description}</p>
        </div>
        <button
          type="button"
          onClick={onExport}
          disabled={busy}
          className="inline-flex w-full items-center justify-center gap-1 rounded bg-indigo-600 px-3 py-2 text-sm font-medium hover:bg-indigo-500 disabled:opacity-50 sm:w-auto sm:py-1.5"
        >
          {busy ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Download size={14} />
          )}
          Download PNG
        </button>
      </div>
      {children}
    </section>
  );
}

/**
 * Scales the 1080×1350 poster down to fit the admin-page preview.
 * The poster stays at its real pixel size — html-to-image reads that
 * size directly and exports at 1080×1350 regardless of the preview.
 */
export function PosterScaledPreview({ children }: { children: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerW, setContainerW] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerW(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Never scale below 480 px — text becomes unreadable. Below that width,
  // the preview scrolls horizontally inside the card.
  const MIN_PREVIEW_W = 480;
  const previewW = Math.max(containerW, MIN_PREVIEW_W);
  const scale = containerW > 0 ? previewW / POSTER_W : 0;
  const previewH = POSTER_H * scale;
  return (
    <div
      ref={containerRef}
      className="rounded border border-slate-800 bg-slate-950"
      style={{
        width: '100%',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        aspectRatio: containerW > 0 ? undefined : `${POSTER_W} / ${POSTER_H}`,
        height: containerW > 0 ? previewH : undefined,
      }}
    >
      <div
        style={{
          width: previewW,
          height: previewH,
          position: 'relative',
        }}
      >
        <div
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top left',
            width: POSTER_W,
            height: POSTER_H,
            visibility: containerW > 0 ? 'visible' : 'hidden',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function PosterFrame({
  accent,
  title,
  weekLabel,
  eyebrow = 'Alliance Hall of Fame',
  children,
}: {
  accent: string;
  title: string;
  weekLabel: string;
  eyebrow?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        width: POSTER_W,
        height: POSTER_H,
        background:
          'radial-gradient(circle at top left, #1e293b 0%, #0b1220 60%, #050810 100%)',
        color: '#e2e8f0',
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        padding: '36px 40px 28px 40px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
      }}
    >
      <PosterHeader title={title} accent={accent} weekLabel={weekLabel} eyebrow={eyebrow} />
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
      <PosterFooter accent={accent} />
    </div>
  );
}

function PosterHeader({
  title,
  accent,
  weekLabel,
  eyebrow,
}: {
  title: string;
  accent: string;
  weekLabel: string;
  eyebrow: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div
          style={{
            fontSize: 14,
            letterSpacing: 3,
            textTransform: 'uppercase',
            color: accent,
            fontWeight: 700,
          }}
        >
          {eyebrow}
        </div>
        <div style={{ fontSize: 44, fontWeight: 800, lineHeight: 1 }}>
          {title}
        </div>
      </div>
      <div
        style={{
          fontSize: 16,
          fontWeight: 600,
          padding: '8px 14px',
          borderRadius: 999,
          border: `2px solid ${accent}55`,
          color: accent,
        }}
      >
        {weekLabel}
      </div>
    </div>
  );
}

function PosterFooter({ accent }: { accent: string }) {
  const allianceLabel = useAllianceLabel();
  const now = new Date().toISOString().slice(0, 10);
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 12,
        color: '#64748b',
        borderTop: `1px solid ${accent}22`,
        paddingTop: 12,
      }}
    >
      <span>Generated {now}</span>
      <span style={{ letterSpacing: 4, textTransform: 'uppercase' }}>
        {allianceLabel}
      </span>
    </div>
  );
}
